<template>
  <div class="min-h-screen bg-gray-50">
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <div class="flex justify-between items-center">
          <h1 class="text-2xl font-semibold text-gray-900">사용자 관리</h1>
          <button
            @click="openAddUserModal"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <i class="fas fa-user-plus text-sm"></i>
            사용자 추가
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="bg-white rounded-lg shadow">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  사용자명
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  설명
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  직무
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  상태
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  최근 로그인
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  작업
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="user in users" :key="user.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="text-sm font-medium text-gray-900">{{
                    user.name
                  }}</span>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">
                    {{ user.description || '-' }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ getUserTypeName(user.type) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'px-2 py-1 text-xs font-medium rounded-full',
                      user.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800',
                    ]"
                  >
                    {{ user.status === 'ACTIVE' ? '활성' : '비활성' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="text-sm text-gray-500">
                    {{ formatDateTime(user.lastLoginDateTime) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                  <button
                    @click="openEditModal(user)"
                    class="text-blue-600 hover:text-blue-800"
                  >
                    수정
                  </button>
                  <button
                    @click="openResetPasswordModal(user)"
                    class="text-blue-600 hover:text-blue-800"
                  >
                    비밀번호 재설정
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">사용자 정보 수정</h3>
        <form @submit.prevent="handleEditSubmit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >사용자명</label
            >
            <input
              :value="editingUser.name"
              disabled
              class="w-full px-3 py-2 bg-gray-100 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >설명</label
            >
            <input
              v-model="editingUser.description"
              type="text"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >직무</label
            >
            <select
              v-model="editingUser.type"
              class="w-full px-3 py-2 border rounded-md"
            >
              <option value="BROKER">브로커</option>
              <option value="MANAGER">매니저</option>
              <option value="TRADER">트레이더</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >상태</label
            >
            <select
              v-model="editingUser.status"
              class="w-full px-3 py-2 border rounded-md"
            >
              <option value="ACTIVE">활성</option>
              <option value="BLOCKED">비활성</option>
            </select>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button
              type="button"
              @click="closeEditModal"
              class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              취소
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              저장
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div
      v-if="showResetPasswordModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">비밀번호 재설정</h3>
        <form @submit.prevent="handleResetPassword" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >새 비밀번호</label
            >
            <input
              v-model="newPassword"
              type="password"
              required
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >비밀번호 확인</label
            >
            <input
              v-model="confirmPassword"
              type="password"
              required
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button
              type="button"
              @click="closeResetPasswordModal"
              class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              취소
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              변경
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add User Modal -->
    <div
      v-if="showAddUserModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">사용자 추가</h3>
        <form @submit.prevent="handleAddUser" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >사용자명</label
            >
            <input
              v-model="newUser.username"
              type="text"
              required
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >설명</label
            >
            <input
              v-model="newUser.description"
              type="text"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >직무</label
            >
            <select
              v-model="newUser.userType"
              class="w-full px-3 py-2 border rounded-md"
            >
              <option value="MANAGER">매니저</option>
              <option value="BROKER">브로커</option>
              <option value="TRADER">트레이더</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >비밀번호</label
            >
            <input
              v-model="newUser.password"
              type="password"
              required
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button
              type="button"
              @click="closeAddUserModal"
              class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              취소
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              추가
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import userService from '@/service/userService';

export default {
  setup() {
    const users = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const showEditModal = ref(false);
    const showResetPasswordModal = ref(false);
    const editingUser = ref(null);
    const selectedUser = ref(null);

    const newPassword = ref('');
    const confirmPassword = ref('');

    const showAddUserModal = ref(false);
    const newUser = ref({
      username: '',
      description: '',
      userType: 'MANAGER',
      password: '',
    });

    const getUserList = async () => {
      try {
        loading.value = true;
        const data = await userService.getUserList();
        users.value = data;
      } catch (err) {
        error.value = '사용자 목록을 불러오는 중 오류가 발생했습니다.';
        console.error('Error fetching users:', err);
      } finally {
        loading.value = false;
      }
    };

    const openEditModal = user => {
      editingUser.value = { ...user };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      editingUser.value = null;
    };

    const handleEditSubmit = async () => {
      try {
        if (!editingUser.value || !editingUser.value.id) {
          throw new Error('사용자 정보가 올바르지 않습니다.');
        }

        await userService.updateUser(editingUser.value.id, {
          description: editingUser.value.description,
          type: editingUser.value.type,
          status: editingUser.value.status,
        });

        await getUserList();
        closeEditModal();
      } catch (err) {
        console.error('Error updating user:', err);
        alert('사용자 정보 수정 중 오류가 발생했습니다.');
      }
    };

    const openResetPasswordModal = user => {
      selectedUser.value = user;
      showResetPasswordModal.value = true;
      newPassword.value = '';
      confirmPassword.value = '';
    };

    const closeResetPasswordModal = () => {
      showResetPasswordModal.value = false;
      selectedUser.value = null;
      newPassword.value = '';
      confirmPassword.value = '';
    };

    const handleResetPassword = async () => {
      if (newPassword.value !== confirmPassword.value) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }

      if (newPassword.value.length < 4) {
        alert('비밀번호는 최소 4자리 이상이어야 합니다.');
        return;
      }

      try {
        await userService.resetPassword(
          selectedUser.value.id,
          newPassword.value
        );
        closeResetPasswordModal();
        alert('비밀번호가 변경되었습니다.');
      } catch (err) {
        console.error('Error resetting password:', err);
        alert('비밀번호 변경 중 오류가 발생했습니다.');
      }
    };

    const getUserTypeName = type => {
      const types = {
        MANAGER: '매니저',
        BROKER: '브로커',
        TRADER: '트레이더',
      };
      return types[type] || type;
    };

    const formatDateTime = dateTime => {
      if (!dateTime) return '로그인 이력 없음';

      const date = new Date(dateTime);
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    };

    const openAddUserModal = () => {
      showAddUserModal.value = true;
      newUser.value = {
        username: '',
        description: '',
        userType: 'MANAGER',
        password: '',
      };
    };

    const closeAddUserModal = () => {
      showAddUserModal.value = false;
      newUser.value = {
        username: '',
        description: '',
        userType: 'MANAGER',
        password: '',
      };
    };

    const handleAddUser = async () => {
      try {
        await userService.registUser(newUser.value);
        await getUserList();
        closeAddUserModal();
      } catch (err) {
        console.error('Error creating user:', err);
        alert(err.message || '사용자 추가 중 오류가 발생했습니다.');
      }
    };

    onMounted(() => {
      getUserList();
    });

    return {
      users,
      loading,
      error,
      showEditModal,
      showResetPasswordModal,
      editingUser,
      selectedUser,
      newPassword,
      confirmPassword,
      getUserList,
      openEditModal,
      closeEditModal,
      handleEditSubmit,
      openResetPasswordModal,
      closeResetPasswordModal,
      handleResetPassword,
      getUserTypeName,
      formatDateTime,
      showAddUserModal,
      newUser,
      openAddUserModal,
      closeAddUserModal,
      handleAddUser,
    };
  },
};
</script>
